/**
 * Luminis protocol v0.
 *
 * This package is intentionally dependency-free and isomorphic: it may be
 * imported by the PC daemon, the Cloudflare Worker, the mobile PWA, plugins and
 * tests. It must not import Node/DOM-specific APIs.
 *
 * Transport rules (see docs/PROTOCOL-v0.md):
 * - Control/approval traffic is wrapped in an E2E envelope (ciphertext).
 * - Plaintext `payload` is only used on trusted local channels/tests.
 * - Every message carries a `type`, unique `id`, and epoch-second `ts`.
 */

export const PROTOCOL_VERSION = 1 as const;
export type ProtocolVersion = typeof PROTOCOL_VERSION;

export const PROTOCOL_MESSAGE_TYPES = [
  'health',
  'pair.begin.request',
  'pair.begin.response',
  'pair.complete.request',
  'pair.complete.response',
  'models.request',
  'models.response',
  'chat.request',
  'chat.delta',
  'chat.done',
  'chat.error',
  'tool.call',
  'tool.result',
  'approval.request',
  'approval.decide',
  'approval.status.request',
  'approval.status.response',
  'exec.started',
  'exec.output',
  'exec.result',
  'event',
  'plugin.install.request',
  'plugin.install.result',
  'plugin.state',
  'session.list.request',
  'session.list.response',
  'session.tail',
  'session.tail.request',
  'session.tail.response',
  'session.command',
  'progress.report',
  'push.subscribe',
  'qq.request',
  'qq.response',
  'memory.request',
  'memory.response',
  'todo.request',
  'todo.response',
  'alarm.request',
  'alarm.response',
  'plan.request',
  'plan.response',
] as const;

export type MessageType = (typeof PROTOCOL_MESSAGE_TYPES)[number];

export type RiskLevel = 'read' | 'write' | 'external' | 'destructive';

/** Four approval grant scopes, matching the documented UX. */
export type ApprovalDecision = 'allow_once' | 'allow_task' | 'allow_15m' | 'allow_always' | 'deny';

export type ThinkingMode = 'enabled' | 'disabled';
export type ThinkingEffort = 'low' | 'high' | 'max';

export const SESSION_SOURCES = ['dsh', 'codex', 'opencode', 'vscode', 'luminis'] as const;
export type SessionSource = (typeof SESSION_SOURCES)[number];
export const SESSION_STATUSES = ['active', 'idle', 'done', 'error'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

/**
 * Session-control capabilities exposed by v0.2.0 (M1.1).
 *
 * Deliberately excludes `terminate` and `create`: the approved M1 scope is
 * "write only, never delete" and new-session creation is deferred. Unknown or
 * unsupported capabilities must be rejected by the daemon.
 */
export const SESSION_CONTROL_CAPABILITIES = ['message', 'interrupt', 'pause', 'resume'] as const;
export type SessionControlCapability = (typeof SESSION_CONTROL_CAPABILITIES)[number];

/**
 * Monitoring-only `session.command` actions (v0.5.0 A+B).
 *
 * `ignore` stops the daemon from scanning/tailing a session and hides it from
 * `session.list`; `unignore` resumes monitoring. They are deliberately NOT
 * session-control capabilities: they never write to a session, never reach a
 * control adapter and do not require the source's control capability
 * advertisement. They are still signed commands (same transcript/nonce/
 * idempotency rules) and must not carry `text` or `mode`.
 */
export const SESSION_MONITORING_COMMAND_ACTIONS = ['ignore', 'unignore'] as const;
export type SessionMonitoringCommandAction = (typeof SESSION_MONITORING_COMMAND_ACTIONS)[number];

export function isSessionMonitoringCommandAction(
  value: unknown,
): value is SessionMonitoringCommandAction {
  return value === 'ignore' || value === 'unignore';
}

/**
 * Actions accepted by `session.command`: the session-write capabilities plus the
 * two monitoring-only actions. Keeping the control capability list separate is
 * intentional (v0.5.0): `ignore`/`unignore` must never be advertised as session
 * control capabilities.
 */
export const SESSION_COMMAND_ACTIONS = [
  ...SESSION_CONTROL_CAPABILITIES,
  ...SESSION_MONITORING_COMMAND_ACTIONS,
] as const;
export type SessionCommandAction = (typeof SESSION_COMMAND_ACTIONS)[number];

/**
 * Per-command message admission mode (`message` only).
 *
 * `steer` interjects into the running open session (non-preemptive); `queue`
 * appends to the session queue instead of steering immediately. A signed
 * command that omits `mode` uses the daemon's configured mode (desktop bridge
 * default `steer`), so pre-mode senders keep their exact behaviour. The
 * protocol/daemon guards reject unknown values and `mode` on non-message
 * commands.
 */
export const SESSION_COMMAND_MODES = ['queue', 'steer'] as const;
export type SessionCommandMode = (typeof SESSION_COMMAND_MODES)[number];

/**
 * Lifecycle status of a `session.command` request, reported via
 * `SESSION_COMMAND_RESULT_EVENT`. `accepted` means durably recorded/queued;
 * terminal states are succeeded/failed/timeout/rejected/unknown.
 */
export const SESSION_COMMAND_STATUSES = [
  'accepted',
  'rejected',
  'succeeded',
  'failed',
  'timeout',
  'unknown',
] as const;
export type SessionCommandStatus = (typeof SESSION_COMMAND_STATUSES)[number];

/**
 * No new MessageType is introduced for control results (the Worker relay
 * forwards the existing `event` type unchanged). Results are `event` payloads
 * with this `event` value and a `SessionCommandResult` in `data`.
 */
export const SESSION_COMMAND_RESULT_EVENT = 'session.command.result' as const;

/** Maximum UTF-8 byte length of `SessionCommandPayload.text` (8 KiB). */
export const SESSION_COMMAND_TEXT_MAX_BYTES = 8192;

/** UUID idempotency key format for `SessionCommandPayload.command_id`. */
export const SESSION_COMMAND_ID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/u;

/** Signature/nonce helpers use URL-safe random strings for the nonce. */
export const SESSION_COMMAND_NONCE_PATTERN = /^[A-Za-z0-9_-]{16,128}$/u;

/** Standard (non-URL-safe) base64 Ed25519 signature; 64 bytes encode to 86 chars + "==". */
export const SESSION_COMMAND_SIGNATURE_PATTERN = /^[A-Za-z0-9+/]{80,128}={0,2}$/u;

export type ErrorCode =
  | 'AUTH_REQUIRED'
  | 'AUTH_INVALID'
  | 'APPROVAL_REQUIRED'
  | 'APPROVAL_EXPIRED'
  | 'APPROVAL_DENIED'
  | 'RISK_BLOCKED'
  | 'TOOL_NOT_FOUND'
  | 'EXEC_TIMEOUT'
  | 'PLUGIN_INVALID'
  | 'RATE_LIMIT'
  | 'INVALID_REQUEST'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'INTERNAL';

export interface ModelSelection {
  provider: string;
  model: string;
  /** DeepSeek thinking mode; defaults come from config, not from this object. */
  thinking?: ThinkingMode;
  /** `low | high | max`; only meaningful when thinking is enabled. */
  effort?: ThinkingEffort;
}

export interface ModelInfo {
  provider: string;
  model: string;
  label?: string;
  context_window?: number;
  max_output_tokens?: number;
  supports_tools?: boolean;
  supports_images?: boolean;
  effort_levels?: ThinkingEffort[];
}

export interface Usage {
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens?: number;
  /** Estimated cost in CNY. */
  cost_cny?: number;
}

export interface DeviceInfo {
  device_id: string;
  name: string;
  platform: string;
  /** Base64 encoded X25519 public key (E2E channel). */
  public_key?: string;
  /**
   * Base64 encoded Ed25519 public key used to verify signed approvals.
   * Additive field for protocol v0 (already used by the PWA/Worker).
   */
  signing_public_key?: string;
}

export interface VerificationSignal {
  name: string;
  ok: boolean;
  detail?: string;
}

export interface VerificationResult {
  ok: boolean;
  methods: VerificationSignal[];
}

/**
 * Common envelope for every protocol message.
 *
 * For E2E channels `ciphertext` contains base64(AES-GCM(payload)) and the
 * plaintext payload must be omitted. Local/test channels may carry `payload`.
 */
export interface Envelope<T = unknown> {
  v: ProtocolVersion;
  id: string;
  type: MessageType;
  /** Unix timestamp in seconds. */
  ts: number;
  device_id?: string;
  /** One-time base64 nonce for replay protection. */
  nonce?: string;
  /** Base64 E2E ciphertext (control/approval channels). */
  ciphertext?: string;
  payload?: T;
}

export interface HealthPayload {
  ok: boolean;
  service: 'daemon' | 'worker' | 'pwa';
  version: string;
  time: number;
  details?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Pairing
// ---------------------------------------------------------------------------

export interface PairBeginRequest {
  code: string;
  device: DeviceInfo;
}

export interface PairBeginResponse {
  ok: boolean;
  server_time: number;
  error?: ErrorCode;
}

export interface PairCompleteRequest {
  code: string;
  device: DeviceInfo;
  /** Signature over the pairing transcript, using the device's Ed25519 key. */
  signature?: string;
}

export interface PairCompleteResponse {
  ok: boolean;
  token?: string;
  expires_at?: number;
  error?: ErrorCode;
}

// ---------------------------------------------------------------------------
// Chat / models
// ---------------------------------------------------------------------------

export interface ChatRequest {
  conversation_id?: string;
  text: string;
  persona_id?: string;
  /** Per-conversation override of the global default. */
  model?: ModelSelection;
  /** Per-turn tool-round budget (1..50); server clamps and defaults to 16. */
  max_tool_rounds?: number;
}

export interface ChatDelta {
  message_id: string;
  seq: number;
  kind: 'content' | 'reasoning';
  delta: string;
}

export interface ChatDone {
  message_id: string;
  model: ModelSelection;
  usage?: Usage;
  finish_reason?: string;
  /**
   * Full final assistant text. The daemon includes it on the relay path so a
   * client that received deltas out of order (queue replay/reconnect) can
   * replace the streamed content with the authoritative text.
   */
  content?: string;
  /** Full final reasoning text (same rationale as `content`). */
  reasoning?: string;
}

export interface ChatError {
  message_id?: string;
  code: ErrorCode;
  message: string;
  retryable?: boolean;
}

export interface ModelsResponse {
  models: ModelInfo[];
  default?: ModelSelection;
}

// ---------------------------------------------------------------------------
// Tools / approval / execution
// ---------------------------------------------------------------------------

export interface ToolCall {
  call_id: string;
  name: string;
  args: Record<string, unknown>;
  risk?: RiskLevel;
}

export interface ToolResult {
  call_id: string;
  ok: boolean;
  result?: unknown;
  error?: string;
  verification?: VerificationResult;
}

export interface ApprovalRequest {
  approval_id: string;
  task_id?: string;
  tool: string;
  /** SHA-256 hex of the canonical tool request (tool + args + task/nonce). */
  args_hash: string;
  risk: RiskLevel;
  summary: string;
  source: 'user' | 'external' | 'system';
  expires_at: number;
  plan?: string[];
}

export interface ApprovalDecide {
  approval_id: string;
  decision: ApprovalDecision;
  /** Ed25519 signature over (approval_id + decision + args_hash + nonce). */
  signature: string;
  nonce: string;
  args_hash: string;
}

/** Additive relay sync: the phone asks the daemon for current approval states. */
export interface ApprovalStatusRequest {
  approval_ids: string[];
}

export type ApprovalWireStatus = 'pending' | 'granted' | 'denied' | 'expired' | 'revoked';

export interface ApprovalExecutionSummary {
  ok: boolean;
  exit_code?: number | null;
  duration_ms?: number;
  stdout?: string;
  stderr?: string;
}

export interface ApprovalStatusItem {
  approval_id: string;
  status: ApprovalWireStatus;
  decision?: ApprovalDecision;
  decided_at?: number;
  execution?: ApprovalExecutionSummary;
}

export interface ApprovalStatusResponse {
  approvals: ApprovalStatusItem[];
}

export interface ExecStarted {
  exec_id: string;
  tool: string;
  pid?: number;
}

export interface ExecOutput {
  exec_id: string;
  seq: number;
  stream: 'stdout' | 'stderr';
  data: string;
}

export interface ExecResult {
  exec_id: string;
  exit_code: number | null;
  ok: boolean;
  duration_ms?: number;
}

export interface EventPayload {
  event: string;
  data?: unknown;
}

// ---------------------------------------------------------------------------
// Plugins
// ---------------------------------------------------------------------------

export interface PluginInstallRequest {
  /** Exactly one of url/path is expected. */
  url?: string;
  path?: string;
  /** Expected SHA-256 hex of the archive/directory manifest. */
  sha256?: string;
  permissions?: string[];
}

export interface PluginInstallResult {
  ok: boolean;
  plugin_id?: string;
  version?: string;
  error?: string;
}

export interface PluginState {
  plugin_id: string;
  version: string;
  enabled: boolean;
  permissions: string[];
  status: 'installed' | 'running' | 'stopped' | 'failed';
}

// ---------------------------------------------------------------------------
// Sessions / progress / push
// ---------------------------------------------------------------------------

export interface SessionListRequest {
  source?: SessionSource;
  active_only?: boolean;
  /** Defaults to 7 days. */
  days?: number;
}

export interface SessionSummary {
  source: SessionSource;
  session_id: string;
  title?: string;
  status: SessionStatus;
  last_event_at?: number;
  tokens?: number;
  cost_cny?: number;
  meta?: Record<string, unknown>;
  /**
   * Additive M1.1 control capability advertisement. Old clients ignore it;
   * M1.1 default is `{ enabled: false, capabilities: [] }`. The phone must not
   * attempt control when `enabled` is false.
   */
  control?: SessionControlInfo;
}

export interface SessionControlInfo {
  enabled: boolean;
  capabilities: SessionControlCapability[];
  /**
   * Additive M1.2b advertisement: the per-command message admission modes this
   * source actually honours (`queue | steer`). Only meaningful when `enabled`
   * is true; the daemon omits the field (and clients must hide any mode
   * selector) when the source has no per-command mode semantics (e.g. the
   * closed-session ACP path). Canonical form: non-empty, deduplicated subset of
   * {@link SESSION_COMMAND_MODES}; empty lists must be omitted.
   */
  modes?: SessionCommandMode[];
  /** Optional human-readable reason when control is disabled/degraded. */
  reason?: string;
}

export interface SessionMonitoringExclusion {
  source: SessionSource;
  session_id: string;
  mode: 'manual' | 'auto';
  reason: 'user' | 'inactive_7d' | 'corrupt';
  updated_at: number;
}

export interface SessionListResponse {
  sessions: SessionSummary[];
  /**
   * Additive v0.5.0: daemon-side monitoring exclusions (server authoritative).
   * Old clients ignore it; the phone uses it to show restore/retry entries.
   */
  monitoring_exclusions?: SessionMonitoringExclusion[];
}

export interface SessionEvent {
  ts: number;
  kind: 'message' | 'tool' | 'status' | 'file' | 'progress';
  role?: string;
  text?: string;
  tool?: string;
  data?: unknown;
}

export interface SessionTail {
  source: SessionSource;
  session_id: string;
  events: SessionEvent[];
  next_cursor?: string;
}

/** Default page size for `session.tail.request` (server clamps to max 200). */
export const SESSION_TAIL_REQUEST_DEFAULT_LIMIT = 100;
/** Hard maximum page size accepted by the daemon for `session.tail.request`. */
export const SESSION_TAIL_REQUEST_MAX_LIMIT = 200;
/** Maximum chars of the opaque cursor accepted on a tail request. */
export const SESSION_TAIL_CURSOR_MAX_CHARS = 256;
/** Maximum chars of a session id accepted on a tail request. */
export const SESSION_TAIL_SESSION_ID_MAX_CHARS = 512;

/**
 * Historical page pull (v0.3.0): the phone asks for an older page of session
 * events instead of relying only on the realtime `session.tail` push subset.
 * The daemon answers with `session.tail.response` ({@link SessionTail} shape).
 */
export interface SessionTailRequest {
  source: SessionSource;
  session_id: string;
  /** Opaque cursor from the previous response's `next_cursor` (`h:<seq>`). */
  cursor?: string;
  /** Page size hint; daemon default 100, clamped to 1..200. */
  limit?: number;
  /**
   * Historical paging hint: when no `cursor` is supplied the daemon returns the
   * NEWEST events with `ts <= before_ts` (INCLUSIVE upper bound; the phone's
   * currently displayed earliest ts). Same-second events deliberately overlap
   * so none are skipped — the phone must deduplicate by event identity.
   * Ignored when `cursor` is present.
   */
  before_ts?: number;
}

/** Additive alias: the response reuses the `SessionTail` wire shape. */
export type SessionTailResponse = SessionTail;

export function isSessionTailRequest(value: unknown): value is SessionTailRequest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (!isSessionSource(candidate['source'])) return false;
  if (
    typeof candidate['session_id'] !== 'string' ||
    candidate['session_id'].length === 0 ||
    candidate['session_id'].length > SESSION_TAIL_SESSION_ID_MAX_CHARS
  ) {
    return false;
  }
  if (candidate['cursor'] !== undefined) {
    if (
      typeof candidate['cursor'] !== 'string' ||
      candidate['cursor'].length > SESSION_TAIL_CURSOR_MAX_CHARS
    ) {
      return false;
    }
  }
  if (candidate['limit'] !== undefined) {
    if (
      typeof candidate['limit'] !== 'number' ||
      !Number.isSafeInteger(candidate['limit']) ||
      candidate['limit'] < 1
    ) {
      return false;
    }
  }
  if (candidate['before_ts'] !== undefined) {
    if (
      typeof candidate['before_ts'] !== 'number' ||
      !Number.isFinite(candidate['before_ts']) ||
      candidate['before_ts'] <= 0
    ) {
      return false;
    }
  }
  return true;
}

export interface SessionCommandPayload {
  source: SessionSource;
  session_id: string;
  command: SessionCommandAction;
  /**
   * Command text. Required (non-empty) when `command === 'message'`; UTF-8
   * bounded to `SESSION_COMMAND_TEXT_MAX_BYTES` (8 KiB) — the daemon enforces
   * this at runtime. Never filled from external content automatically.
   */
  text?: string;
  /**
   * Per-command message admission mode. **Valid only when
   * `command === 'message'`**: `steer` interjects, `queue` appends. When
   * omitted the daemon's configured mode is used (default `steer`); the field
   * is part of the signed transcript only when defined, so mode-less payloads
   * remain byte-identical to their pre-mode signatures.
   */
  mode?: SessionCommandMode;
  /**
   * UUID idempotency key. **Required when session control is enabled**; the
   * persisted command ledger deduplicates retries across daemon restarts and
   * returns the recorded outcome instead of re-dispatching.
   */
  command_id?: string;
  /**
   * URL-safe random nonce used in the Ed25519 signature transcript (single-use;
   * the server also enforces a 5-minute window around `issued_at`).
   * **Required when session control is enabled.**
   */
  nonce?: string;
  /**
   * Unix seconds timestamp generated by the phone when it signs the command.
   * **Required when session control is enabled**, part of the signed canonical
   * transcript, and the ONLY freshness source: the server rejects missing,
   * stale (older than the window) or future (ahead of the window) values.
   * Unsigned HTTP headers/envelope timestamps are never used as a substitute.
   */
  issued_at?: number;
  /**
   * Standard-base64 Ed25519 signature over
   * `sessionCommandSigningPayload(payload, sha256Hex(text))`, made with the
   * phone's signing key. **Required when session control is enabled.**
   */
  signature?: string;
  /** Optional target-state guard checked against SessionStore before dispatch. */
  expected_state?: SessionStatus;
  /** Capability hint only; the server's control registry is authoritative. */
  capability_hint?: SessionControlCapability;
  /** Optional approval id when the command is bound to an approval record. */
  approval_id?: string;
}

export interface SessionCommandResult {
  command_id: string;
  source: SessionSource;
  session_id: string;
  status: SessionCommandStatus;
  code?: ErrorCode;
  /** Already-redacted detail (no command text, no local paths). */
  detail?: string;
  audit_id?: string;
  /** Unix timestamp in seconds. */
  ts: number;
}

/**
 * Canonical Ed25519 transcript for a `session.command` (M1-PLAN §5).
 *
 * `${command_id}\n${source}\n${session_id}\n${command}\n${text_hash}\n${nonce}\n` +
 * `${issued_at || ''}\n${expected_state || ''}\n${capability_hint || ''}\n` +
 * `${approval_id || ''}`
 *
 * When the payload defines `mode` (message commands only), exactly one extra
 * field `\n${mode}` is appended. The field is appended ONLY when defined, so
 * mode-less payloads keep the exact pre-mode transcript byte-for-byte and old
 * signatures stay valid (backward compatible).
 *
 * `textHash` is the lowercase hex SHA-256 of the UTF-8 `text` (empty string
 * hashed when absent); the phone and daemon must compute it identically and
 * compare byte-for-byte. `issued_at` is signed so a freshness window cannot be
 * bypassed or substituted with an unsigned header/transport timestamp.
 * Signatures use the standard-base64 Ed25519 format.
 */
export function sessionCommandSigningPayload(
  payload: SessionCommandPayload,
  textHash: string,
): string {
  const base = [
    payload.command_id ?? '',
    payload.source,
    payload.session_id,
    payload.command,
    textHash,
    payload.nonce ?? '',
    payload.issued_at === undefined ? '' : String(payload.issued_at),
    payload.expected_state ?? '',
    payload.capability_hint ?? '',
    payload.approval_id ?? '',
  ].join('\n');
  return payload.mode === undefined ? base : `${base}\n${payload.mode}`;
}

export interface ProgressReport {
  task_id: string;
  phase: string;
  percent?: number;
  note?: string;
  tokens?: number;
  cost_cny?: number;
  ts?: number;
}

export interface PushSubscribePayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

// ---------------------------------------------------------------------------
// QQ management over the relay (v0.5.0)
// ---------------------------------------------------------------------------

/**
 * Operations the paired phone may request from the daemon QQ controller over
 * the E2E relay. They mirror the loopback `/qq/*` API exactly:
 * - `status`      -> `QqStatus` (no secrets, no message text);
 * - `permissions` -> body is validated by the daemon `QqController` and the
 *                    updated `QqStatus` is returned;
 * - `disconnect`  -> `{ ok: true }` after the daemon stops the bot transport.
 *
 * Unknown operations and malformed payloads fail closed with a fixed
 * `INVALID_REQUEST` response; no request body is ever echoed in an error.
 */
export const QQ_MANAGEMENT_OPS = ['status', 'permissions', 'disconnect'] as const;
export type QqManagementOp = (typeof QQ_MANAGEMENT_OPS)[number];

export const QQ_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/u;

export interface QqRequestPayload {
  /** Correlation id generated by the phone; echoed in `qq.response`. */
  request_id: string;
  op: QqManagementOp;
  /** Only used by `permissions`; validated by the daemon controller. */
  body?: Record<string, unknown>;
}

export interface QqResponsePayload {
  request_id: string;
  ok: boolean;
  /** `QqStatus` for status/permissions, `{ ok: true }` for disconnect. */
  data?: unknown;
  error?: {
    code: ErrorCode;
    /** Fixed daemon message (never echoes request data). */
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Long-term memory management over the relay (v0.5.0)
// ---------------------------------------------------------------------------

/**
 * Operations the paired phone may request from the daemon memory store over the
 * E2E relay, mirroring the loopback `/memories` API:
 * - `list`    -> `data: { memories: [...] }`;
 * - `delete`  -> `data: { memories: [...] }` (bounded `id`, 404 when unknown);
 * - `clear`   -> `data: { ok: true }`;
 * - `approve` -> `data: { memories: [...] }` (pending -> active, 404 when unknown).
 *
 * Unknown ops and malformed payloads fail closed with a fixed `INVALID_REQUEST`
 * response; memory text never appears in an error message or audit metadata.
 */
export const MEMORY_MANAGEMENT_OPS = ['list', 'delete', 'clear', 'approve'] as const;
export type MemoryManagementOp = (typeof MEMORY_MANAGEMENT_OPS)[number];

export const MEMORY_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/u;
/** Memory ids are daemon-generated (`mem_…`); bounded/opaque to the phone. */
export const MEMORY_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/u;

export interface MemoryRequestPayload {
  /** Correlation id generated by the phone; echoed in `memory.response`. */
  request_id: string;
  op: MemoryManagementOp;
  /** Required by `delete`/`approve`; forbidden for `list`/`clear`. */
  id?: string;
}

export interface MemoryResponsePayload {
  request_id: string;
  ok: boolean;
  /** `{ memories }` for list/delete/approve, `{ ok: true }` for clear. */
  data?: unknown;
  error?: {
    code: ErrorCode;
    /** Fixed daemon message (never echoes memory text or ids). */
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Todo / alarm management over the relay (v0.6.0)
// ---------------------------------------------------------------------------

/**
 * Operations the paired phone may request from the daemon todo store over the
 * E2E relay, mirroring the loopback `/todos` API exactly:
 * - `list`   -> `data: { todos }`;
 * - `create` -> `data: { todo }` (body: title/notes/due_at/remind_at/priority);
 * - `update` -> `data: { todo }` (bounded `id` + partial body);
 * - `delete` -> `data: { ok: true, todos }` (remaining list);
 * - `toggle` -> `data: { todo }` (bounded `id`, pending <-> done).
 *
 * Unknown ops, malformed ids and invalid bodies fail closed with the same
 * `INVALID_REQUEST`/`NOT_FOUND` codes as HTTP. Todo text never appears in an
 * error message or audit metadata.
 */
export const TODO_MANAGEMENT_OPS = ['list', 'create', 'update', 'delete', 'toggle'] as const;
export type TodoManagementOp = (typeof TODO_MANAGEMENT_OPS)[number];

/**
 * Operations the paired phone may request from the daemon alarm store over the
 * E2E relay, mirroring the loopback `/alarms` API exactly:
 * - `list`   -> `data: { alarms }`;
 * - `create` -> `data: { alarm }` (body includes repeat_days/enabled/sound);
 * - `update` -> `data: { alarm }` (bounded `id` + partial body);
 * - `delete` -> `data: { ok: true, alarms }` (remaining list);
 * - `fired`  -> `data: { alarm }` (daemon advances `next_at`/disables one-shot).
 *
 * `next_at` is daemon-authoritative and is never accepted from the client.
 */
export const ALARM_MANAGEMENT_OPS = ['list', 'create', 'update', 'delete', 'fired'] as const;
export type AlarmManagementOp = (typeof ALARM_MANAGEMENT_OPS)[number];

/** Correlation id generated by the phone (same alphabet as QQ/memory). */
export const TODO_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/u;
export const ALARM_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/u;

/**
 * Opaque, bounded todo/alarm row id. Deliberately looser than the daemon's
 * storage pattern (`todo_<hex>`): the daemon repository remains authoritative
 * and maps a malformed id to the same `INVALID_REQUEST` as HTTP, while an
 * unknown-but-well-formed id is `NOT_FOUND`.
 */
export const TODO_ALARM_ENTITY_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/u;

/** Upper bound on the JSON-encoded create/update body (8 KiB). */
export const TODO_ALARM_BODY_MAX_BYTES = 8192;

export interface TodoRequestPayload {
  /** Correlation id generated by the phone; echoed in `todo.response`. */
  request_id: string;
  op: TodoManagementOp;
  /** Required by update/delete/toggle; forbidden for list/create. */
  id?: string;
  /** Required by create/update; forbidden for list/delete/toggle. */
  body?: Record<string, unknown>;
}

export interface TodoResponsePayload {
  request_id: string;
  ok: boolean;
  /** `{ todos }`, `{ todo }` or `{ ok: true, todos }` — exactly the HTTP shape. */
  data?: unknown;
  error?: {
    code: ErrorCode;
    /** Fixed daemon message (never echoes todo text). */
    message: string;
  };
}

export interface AlarmRequestPayload {
  /** Correlation id generated by the phone; echoed in `alarm.response`. */
  request_id: string;
  op: AlarmManagementOp;
  /** Required by update/delete/fired; forbidden for list/create. */
  id?: string;
  /** Required by create/update; forbidden for list/delete/fired. */
  body?: Record<string, unknown>;
}

export interface AlarmResponsePayload {
  request_id: string;
  ok: boolean;
  /** `{ alarms }`, `{ alarm }` or `{ ok: true, alarms }` — exactly HTTP shape. */
  data?: unknown;
  error?: {
    code: ErrorCode;
    /** Fixed daemon message (never echoes alarm text). */
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Plans / agenda management over the relay (v0.6.0)
// ---------------------------------------------------------------------------

/**
 * Operations the paired phone may request from the daemon plans/agenda backend
 * over the E2E relay, mirroring the loopback HTTP API exactly:
 * - `list`   -> `data: { plans }` (active list, same as GET /plans);
 * - `create` -> `data: { plan }` (body: text/notes/start_at/due_at/expires_at);
 * - `update` -> `data: { plan }` (bounded `id` + partial body);
 * - `done`   -> `data: { plan }` (bounded `id`, idempotent; completed_at kept);
 * - `delete` -> `data: { ok: true, plans }` (remaining active list);
 * - `agenda` -> `data: ApiAgenda` (same shape as GET /agenda; optional `date`);
 * - `dismiss_assignment` -> `data: { ok: true, assignments }` (bounded
 *   assignment `id`; hides it from the agenda locally, never touches Learn);
 * - `restore_assignment` -> `data: { ok: true, assignments }` (un-hides it).
 *
 * Unknown ops, malformed ids/bodies/dates and unknown plan/assignment ids fail
 * closed with the same `INVALID_REQUEST`/`NOT_FOUND` codes as HTTP. Plan text
 * never appears in an error message or audit metadata. The assignment ops carry
 * no body and no date; the response `assignments` rows use the same
 * `ApiAgendaAssignment` shape as `GET /agenda`.
 */
export const PLAN_MANAGEMENT_OPS = [
  'list',
  'create',
  'update',
  'done',
  'delete',
  'agenda',
  'dismiss_assignment',
  'restore_assignment',
] as const;
export type PlanManagementOp = (typeof PLAN_MANAGEMENT_OPS)[number];

/** Correlation id generated by the phone (same alphabet as QQ/memory/todo). */
export const PLAN_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/u;

/**
 * Opaque, bounded entity id (plan id `plan_<hex>`, Learn assignment id
 * `learn_<hex>`). Deliberately looser than the daemon storage pattern: the
 * repository remains authoritative and maps malformed ids to INVALID_REQUEST
 * while unknown-but-well-formed ids are NOT_FOUND.
 */
export const PLAN_ENTITY_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/u;

/** Local calendar date for the `agenda` op (`YYYY-MM-DD`, optional = today). */
export const PLAN_AGENDA_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

export interface PlanRequestPayload {
  /** Correlation id generated by the phone; echoed in `plan.response`. */
  request_id: string;
  op: PlanManagementOp;
  /** Required by update/done/delete/dismiss_assignment/restore_assignment. */
  id?: string;
  /** Required by create/update; forbidden for the other ops; optional list. */
  body?: Record<string, unknown>;
  /** Only for agenda; `YYYY-MM-DD`; omitted = daemon's local today. */
  date?: string;
}

export interface PlanResponsePayload {
  request_id: string;
  ok: boolean;
  /** `{ plans }` / `{ plan }` / `{ ok: true, plans }` / `ApiAgenda` — HTTP shape. */
  data?: unknown;
  error?: {
    code: ErrorCode;
    /** Fixed daemon message (never echoes plan text). */
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Guards & helpers
// ---------------------------------------------------------------------------

const MESSAGE_TYPE_SET: ReadonlySet<string> = new Set(PROTOCOL_MESSAGE_TYPES);

export function isMessageType(value: unknown): value is MessageType {
  return typeof value === 'string' && MESSAGE_TYPE_SET.has(value);
}

export function isEnvelope(value: unknown): value is Envelope {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate.v === PROTOCOL_VERSION &&
    typeof candidate.id === 'string' &&
    candidate.id.length > 0 &&
    isMessageType(candidate.type) &&
    typeof candidate.ts === 'number' &&
    Number.isFinite(candidate.ts)
  );
}

export interface EnvelopeInit {
  id: string;
  ts?: number;
  device_id?: string;
  nonce?: string;
  ciphertext?: string;
  payload?: unknown;
}

const SESSION_SOURCE_SET: ReadonlySet<string> = new Set(SESSION_SOURCES);
const SESSION_STATUS_SET: ReadonlySet<string> = new Set(SESSION_STATUSES);
const SESSION_COMMAND_ACTION_SET: ReadonlySet<string> = new Set(SESSION_COMMAND_ACTIONS);
const SESSION_CONTROL_CAPABILITY_SET: ReadonlySet<string> = new Set(SESSION_CONTROL_CAPABILITIES);
const SESSION_COMMAND_STATUS_SET: ReadonlySet<string> = new Set(SESSION_COMMAND_STATUSES);
const SESSION_COMMAND_MODE_SET: ReadonlySet<string> = new Set(SESSION_COMMAND_MODES);

export function isSessionSource(value: unknown): value is SessionSource {
  return typeof value === 'string' && SESSION_SOURCE_SET.has(value);
}

export function isSessionStatus(value: unknown): value is SessionStatus {
  return typeof value === 'string' && SESSION_STATUS_SET.has(value);
}

export function isSessionCommandAction(value: unknown): value is SessionCommandAction {
  return typeof value === 'string' && SESSION_COMMAND_ACTION_SET.has(value);
}

export function isSessionControlCapability(value: unknown): value is SessionControlCapability {
  return typeof value === 'string' && SESSION_CONTROL_CAPABILITY_SET.has(value);
}

export function isSessionCommandStatus(value: unknown): value is SessionCommandStatus {
  return typeof value === 'string' && SESSION_COMMAND_STATUS_SET.has(value);
}

/** Guard for `SessionCommandPayload.mode` (only meaningful on message commands). */
export function isSessionCommandMode(value: unknown): value is SessionCommandMode {
  return typeof value === 'string' && SESSION_COMMAND_MODE_SET.has(value);
}

export function isSessionControlInfo(value: unknown): value is SessionControlInfo {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.enabled !== 'boolean' ||
    !Array.isArray(candidate.capabilities) ||
    !candidate.capabilities.every((capability) => isSessionControlCapability(capability)) ||
    (candidate.reason !== undefined && typeof candidate.reason !== 'string')
  ) {
    return false;
  }
  if (candidate.modes === undefined) return true;
  // Additive mode advertisement is only meaningful for enabled control and must
  // already be canonical (non-empty, deduplicated, queue/steer only). Producers
  // that advertise an empty/unknown set must omit the field instead.
  if (candidate.enabled !== true || !Array.isArray(candidate.modes)) return false;
  if (candidate.modes.length === 0) return false;
  const seen = new Set<string>();
  for (const mode of candidate.modes) {
    if (!isSessionCommandMode(mode)) return false;
    if (seen.has(mode)) return false;
    seen.add(mode);
  }
  return true;
}

/** UTF-8 byte length (mirrors the daemon's 8 KiB command-text enforcement). */
function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

/**
 * Lenient `session.command` shape guard: validates the legacy fields plus the
 * optional M1.1 fields when present. It intentionally accepts payloads without
 * `command_id`/`nonce`/`signature` so pre-M1.1 senders keep type compatibility;
 * the daemon always requires the signed form when control is enabled.
 *
 * `mode` is accepted only on `message` commands and only from the fixed
 * `queue | steer` set; any other placement/value makes the whole payload
 * invalid (the caller returns a fixed error without echoing the value).
 */
export function isSessionCommandPayload(value: unknown): value is SessionCommandPayload {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (!isSessionSource(candidate.source)) return false;
  if (typeof candidate.session_id !== 'string') return false;
  if (candidate.session_id.length === 0 || candidate.session_id.length > 512) return false;
  if (!isSessionCommandAction(candidate.command)) return false;
  if (candidate.text !== undefined && typeof candidate.text !== 'string') return false;
  // Monitoring-only actions never carry command text. `mode` is message-only
  // for every action (checked below), so ignore/unignore reject both.
  if (isSessionMonitoringCommandAction(candidate.command) && candidate.text !== undefined) {
    return false;
  }
  if (candidate.mode !== undefined) {
    if (candidate.command !== 'message') return false;
    if (!isSessionCommandMode(candidate.mode)) return false;
  }
  for (const key of ['command_id', 'nonce', 'signature', 'approval_id'] as const) {
    if (candidate[key] !== undefined && typeof candidate[key] !== 'string') return false;
  }
  if (
    candidate.issued_at !== undefined &&
    (typeof candidate.issued_at !== 'number' ||
      !Number.isSafeInteger(candidate.issued_at) ||
      candidate.issued_at <= 0)
  ) {
    return false;
  }
  if (candidate.expected_state !== undefined && !isSessionStatus(candidate.expected_state)) {
    return false;
  }
  if (
    candidate.capability_hint !== undefined &&
    !isSessionControlCapability(candidate.capability_hint)
  ) {
    return false;
  }
  return true;
}

/**
 * Strict signed-command guard for the control-enabled path: requires a UUID
 * `command_id`, URL-safe `nonce`, signed integer `issued_at` (Unix seconds),
 * standard-base64 Ed25519 `signature`, and an 8 KiB UTF-8 bound on `text`
 * (message commands must carry non-empty text; other actions must not carry
 * text).
 */
export function isSignedSessionCommandPayload(value: unknown): value is SessionCommandPayload & {
  command_id: string;
  nonce: string;
  issued_at: number;
  signature: string;
} {
  if (!isSessionCommandPayload(value)) return false;
  const payload = value;
  if (payload.command_id === undefined || !SESSION_COMMAND_ID_PATTERN.test(payload.command_id)) {
    return false;
  }
  if (payload.nonce === undefined || !SESSION_COMMAND_NONCE_PATTERN.test(payload.nonce)) {
    return false;
  }
  if (
    payload.issued_at === undefined ||
    !Number.isSafeInteger(payload.issued_at) ||
    payload.issued_at <= 0
  ) {
    return false;
  }
  if (
    payload.signature === undefined ||
    payload.signature.length % 4 !== 0 ||
    !SESSION_COMMAND_SIGNATURE_PATTERN.test(payload.signature)
  ) {
    return false;
  }
  if (payload.command === 'message') {
    if (payload.text === undefined || payload.text.length === 0) return false;
    if (utf8ByteLength(payload.text) > SESSION_COMMAND_TEXT_MAX_BYTES) return false;
  } else if (payload.text !== undefined) {
    return false;
  }
  return true;
}

export function isSessionCommandResult(value: unknown): value is SessionCommandResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.command_id !== 'string' || candidate.command_id.length === 0) return false;
  if (!isSessionSource(candidate.source)) return false;
  if (typeof candidate.session_id !== 'string' || candidate.session_id.length === 0) return false;
  if (!isSessionCommandStatus(candidate.status)) return false;
  if (typeof candidate.ts !== 'number' || !Number.isFinite(candidate.ts)) return false;
  if (candidate.code !== undefined && typeof candidate.code !== 'string') return false;
  if (candidate.detail !== undefined && typeof candidate.detail !== 'string') return false;
  if (candidate.audit_id !== undefined && typeof candidate.audit_id !== 'string') return false;
  return true;
}

// ---------------------------------------------------------------------------
// QQ management guards
// ---------------------------------------------------------------------------

const QQ_MANAGEMENT_OP_SET: ReadonlySet<string> = new Set(QQ_MANAGEMENT_OPS);

export function isQqManagementOp(value: unknown): value is QqManagementOp {
  return typeof value === 'string' && QQ_MANAGEMENT_OP_SET.has(value);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Strict `qq.request` guard: bounded request id, known op, object body only. */
export function isQqRequestPayload(value: unknown): value is QqRequestPayload {
  if (!isPlainRecord(value)) return false;
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !QQ_REQUEST_ID_PATTERN.test(requestId)) return false;
  if (!isQqManagementOp(value['op'])) return false;
  const body = value['body'];
  if (body !== undefined && !isPlainRecord(body)) return false;
  return true;
}

/** Strict `qq.response` guard (data is re-validated by the caller). */
export function isQqResponsePayload(value: unknown): value is QqResponsePayload {
  if (!isPlainRecord(value)) return false;
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !QQ_REQUEST_ID_PATTERN.test(requestId)) return false;
  if (typeof value['ok'] !== 'boolean') return false;
  const data = value['data'];
  if (data !== undefined && !isPlainRecord(data)) return false;
  const error = value['error'];
  if (error !== undefined) {
    if (!isPlainRecord(error)) return false;
    const code = error['code'];
    const message = error['message'];
    if (typeof code !== 'string' || code.length === 0 || code.length > 64) return false;
    if (typeof message !== 'string' || message.length > 300) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Long-term memory guards
// ---------------------------------------------------------------------------

const MEMORY_MANAGEMENT_OP_SET: ReadonlySet<string> = new Set(MEMORY_MANAGEMENT_OPS);

export function isMemoryManagementOp(value: unknown): value is MemoryManagementOp {
  return typeof value === 'string' && MEMORY_MANAGEMENT_OP_SET.has(value);
}

/** Strict `memory.request` guard: bounded ids, known op, op-appropriate id. */
export function isMemoryRequestPayload(value: unknown): value is MemoryRequestPayload {
  if (!isPlainRecord(value)) return false;
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !MEMORY_REQUEST_ID_PATTERN.test(requestId)) return false;
  const op = value['op'];
  if (!isMemoryManagementOp(op)) return false;
  const id = value['id'];
  if (op === 'delete' || op === 'approve') {
    if (typeof id !== 'string' || !MEMORY_ID_PATTERN.test(id)) return false;
    return true;
  }
  // list/clear never carry an id.
  return id === undefined;
}

/** Strict `memory.response` guard (data rows are re-validated by the caller). */
export function isMemoryResponsePayload(value: unknown): value is MemoryResponsePayload {
  if (!isPlainRecord(value)) return false;
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !MEMORY_REQUEST_ID_PATTERN.test(requestId)) return false;
  if (typeof value['ok'] !== 'boolean') return false;
  const data = value['data'];
  if (data !== undefined && !isPlainRecord(data)) return false;
  const error = value['error'];
  if (error !== undefined) {
    if (!isPlainRecord(error)) return false;
    const code = error['code'];
    const message = error['message'];
    if (typeof code !== 'string' || code.length === 0 || code.length > 64) return false;
    if (typeof message !== 'string' || message.length > 300) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Todo / alarm management guards
// ---------------------------------------------------------------------------

const TODO_MANAGEMENT_OP_SET: ReadonlySet<string> = new Set(TODO_MANAGEMENT_OPS);
const ALARM_MANAGEMENT_OP_SET: ReadonlySet<string> = new Set(ALARM_MANAGEMENT_OPS);

const TODO_ID_REQUIRED_OPS: ReadonlySet<string> = new Set(['update', 'delete', 'toggle']);
const TODO_BODY_REQUIRED_OPS: ReadonlySet<string> = new Set(['create', 'update']);
const ALARM_ID_REQUIRED_OPS: ReadonlySet<string> = new Set(['update', 'delete', 'fired']);
const ALARM_BODY_REQUIRED_OPS: ReadonlySet<string> = new Set(['create', 'update']);

const TODO_ALARM_REQUEST_FIELDS: ReadonlySet<string> = new Set(['request_id', 'op', 'id', 'body']);
const TODO_ALARM_RESPONSE_FIELDS: ReadonlySet<string> = new Set([
  'request_id',
  'ok',
  'data',
  'error',
]);
/** Hard ceiling for a validated response payload (Worker relay frame headroom). */
export const TODO_ALARM_RESPONSE_MAX_BYTES = 256 * 1024;

export function isTodoManagementOp(value: unknown): value is TodoManagementOp {
  return typeof value === 'string' && TODO_MANAGEMENT_OP_SET.has(value);
}

export function isAlarmManagementOp(value: unknown): value is AlarmManagementOp {
  return typeof value === 'string' && ALARM_MANAGEMENT_OP_SET.has(value);
}

/**
 * Strict, bounded JSON value check for a request `body`. Arrays are capped at
 * 16 items (only `repeat_days` is an array in practice), strings at 4 KiB,
 * objects at 16 keys and nesting at 3 levels. The daemon still enforces the
 * exact HTTP field rules; this only rejects pathological payloads up front.
 */
function isBoundedRequestBodyValue(value: unknown, depth: number): boolean {
  if (value === null) return true;
  if (typeof value === 'string') return value.length <= 4096;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) {
    return value.length <= 16 && value.every((item) => isBoundedRequestBodyValue(item, depth + 1));
  }
  if (!isPlainRecord(value) || depth >= 3) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= 16 &&
    entries.every(([key, item]) => key.length <= 64 && isBoundedRequestBodyValue(item, depth + 1))
  );
}

function isBoundedRequestBody(value: unknown): value is Record<string, unknown> {
  if (!isPlainRecord(value)) return false;
  try {
    if (utf8ByteLength(JSON.stringify(value)) > TODO_ALARM_BODY_MAX_BYTES) return false;
  } catch {
    return false;
  }
  // The top-level body is already a plain record; validate its values.
  return Object.entries(value).every(
    ([key, item]) => key.length <= 64 && isBoundedRequestBodyValue(item, 1),
  );
}

/**
 * Bounded response `data` check. Lists are authoritative daemon output and may
 * legitimately hold thousands of rows, so the limits are much larger than a
 * request body (still below the Worker relay frame limit).
 */
function isBoundedResponseDataValue(value: unknown, depth: number): boolean {
  if (value === null) return true;
  if (typeof value === 'string') return value.length <= 8192;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) {
    return (
      value.length <= 5000 && value.every((item) => isBoundedResponseDataValue(item, depth + 1))
    );
  }
  if (!isPlainRecord(value) || depth >= 6) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= 128 &&
    entries.every(([key, item]) => key.length <= 128 && isBoundedResponseDataValue(item, depth + 1))
  );
}

function isBoundedResponseData(value: unknown): value is Record<string, unknown> {
  if (!isPlainRecord(value)) return false;
  try {
    if (utf8ByteLength(JSON.stringify(value)) > TODO_ALARM_RESPONSE_MAX_BYTES) return false;
  } catch {
    return false;
  }
  return isBoundedResponseDataValue(value, 1);
}

function isTodoAlarmResponseError(value: unknown): boolean {
  if (!isPlainRecord(value)) return false;
  const code = value['code'];
  const message = value['message'];
  return (
    typeof code === 'string' &&
    code.length > 0 &&
    code.length <= 64 &&
    typeof message === 'string' &&
    message.length <= 300
  );
}

function isTodoAlarmRequestEnvelope(
  value: unknown,
  pattern: RegExp,
  isOp: (op: unknown) => boolean,
  idRequiredOps: ReadonlySet<string>,
  bodyRequiredOps: ReadonlySet<string>,
): boolean {
  if (!isPlainRecord(value)) return false;
  for (const key of Object.keys(value)) {
    if (!TODO_ALARM_REQUEST_FIELDS.has(key)) return false;
  }
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !pattern.test(requestId)) return false;
  const op = value['op'];
  if (!isOp(op) || typeof op !== 'string') return false;

  const id = value['id'];
  if (idRequiredOps.has(op)) {
    if (typeof id !== 'string' || !TODO_ALARM_ENTITY_ID_PATTERN.test(id)) return false;
  } else if (id !== undefined) {
    return false;
  }

  const body = value['body'];
  if (bodyRequiredOps.has(op)) {
    if (!isBoundedRequestBody(body)) return false;
  } else if (body !== undefined) {
    return false;
  }
  return true;
}

/** Strict `todo.request` guard: known op, op-appropriate id/body, bounded body. */
export function isTodoRequestPayload(value: unknown): value is TodoRequestPayload {
  return isTodoAlarmRequestEnvelope(
    value,
    TODO_REQUEST_ID_PATTERN,
    isTodoManagementOp,
    TODO_ID_REQUIRED_OPS,
    TODO_BODY_REQUIRED_OPS,
  );
}

/** Strict `alarm.request` guard: known op, op-appropriate id/body, bounded body. */
export function isAlarmRequestPayload(value: unknown): value is AlarmRequestPayload {
  return isTodoAlarmRequestEnvelope(
    value,
    ALARM_REQUEST_ID_PATTERN,
    isAlarmManagementOp,
    ALARM_ID_REQUIRED_OPS,
    ALARM_BODY_REQUIRED_OPS,
  );
}

function isTodoAlarmResponseEnvelope(value: unknown, pattern: RegExp): boolean {
  if (!isPlainRecord(value)) return false;
  for (const key of Object.keys(value)) {
    if (!TODO_ALARM_RESPONSE_FIELDS.has(key)) return false;
  }
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !pattern.test(requestId)) return false;
  if (typeof value['ok'] !== 'boolean') return false;
  const data = value['data'];
  if (data !== undefined && !isBoundedResponseData(data)) return false;
  const error = value['error'];
  if (error !== undefined && !isTodoAlarmResponseError(error)) return false;
  return true;
}

/** Strict `todo.response` guard (row fields are re-validated by the caller). */
export function isTodoResponsePayload(value: unknown): value is TodoResponsePayload {
  return isTodoAlarmResponseEnvelope(value, TODO_REQUEST_ID_PATTERN);
}

/** Strict `alarm.response` guard (row fields are re-validated by the caller). */
export function isAlarmResponsePayload(value: unknown): value is AlarmResponsePayload {
  return isTodoAlarmResponseEnvelope(value, ALARM_REQUEST_ID_PATTERN);
}

// ---------------------------------------------------------------------------
// Plans / agenda management guards
// ---------------------------------------------------------------------------

const PLAN_MANAGEMENT_OP_SET: ReadonlySet<string> = new Set(PLAN_MANAGEMENT_OPS);
const PLAN_ID_REQUIRED_OPS: ReadonlySet<string> = new Set([
  'update',
  'done',
  'delete',
  'dismiss_assignment',
  'restore_assignment',
]);
const PLAN_BODY_REQUIRED_OPS: ReadonlySet<string> = new Set(['create', 'update']);
const PLAN_REQUEST_FIELDS: ReadonlySet<string> = new Set([
  'request_id',
  'op',
  'id',
  'body',
  'date',
]);
const PLAN_RESPONSE_FIELDS: ReadonlySet<string> = new Set(['request_id', 'ok', 'data', 'error']);
/** Same hard ceiling as todo/alarm (Worker relay frame headroom). */
export const PLAN_RESPONSE_MAX_BYTES = TODO_ALARM_RESPONSE_MAX_BYTES;
const MIN_PLAN_AGENDA_YEAR = 1970;
const MAX_PLAN_AGENDA_YEAR = 2100;

export function isPlanManagementOp(value: unknown): value is PlanManagementOp {
  return typeof value === 'string' && PLAN_MANAGEMENT_OP_SET.has(value);
}

/** Calendar-valid `YYYY-MM-DD` within the daemon agenda's supported range. */
export function isPlanAgendaDate(value: unknown): value is string {
  if (typeof value !== 'string' || !PLAN_AGENDA_DATE_PATTERN.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (year < MIN_PLAN_AGENDA_YEAR || year > MAX_PLAN_AGENDA_YEAR) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * Strict `plan.request` guard: known op, op-appropriate id/body/date, bounded
 * body and a calendar-valid agenda date. Unknown fields are rejected up front.
 */
export function isPlanRequestPayload(value: unknown): value is PlanRequestPayload {
  if (!isPlainRecord(value)) return false;
  for (const key of Object.keys(value)) {
    if (!PLAN_REQUEST_FIELDS.has(key)) return false;
  }
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !PLAN_REQUEST_ID_PATTERN.test(requestId)) return false;
  const op = value['op'];
  if (!isPlanManagementOp(op)) return false;

  const id = value['id'];
  if (PLAN_ID_REQUIRED_OPS.has(op)) {
    if (typeof id !== 'string' || !PLAN_ENTITY_ID_PATTERN.test(id)) return false;
  } else if (id !== undefined) {
    return false;
  }

  const body = value['body'];
  if (PLAN_BODY_REQUIRED_OPS.has(op)) {
    if (!isBoundedRequestBody(body)) return false;
  } else if (op === 'list') {
    if (body !== undefined && !isBoundedRequestBody(body)) return false;
  } else if (body !== undefined) {
    return false;
  }

  const date = value['date'];
  if (op === 'agenda') {
    if (date !== undefined && !isPlanAgendaDate(date)) return false;
  } else if (date !== undefined) {
    return false;
  }
  return true;
}

/** Strict `plan.response` guard (row fields are re-validated by the caller). */
export function isPlanResponsePayload(value: unknown): value is PlanResponsePayload {
  if (!isPlainRecord(value)) return false;
  for (const key of Object.keys(value)) {
    if (!PLAN_RESPONSE_FIELDS.has(key)) return false;
  }
  const requestId = value['request_id'];
  if (typeof requestId !== 'string' || !PLAN_REQUEST_ID_PATTERN.test(requestId)) return false;
  if (typeof value['ok'] !== 'boolean') return false;
  const data = value['data'];
  if (data !== undefined && !isBoundedResponseData(data)) return false;
  const error = value['error'];
  if (error !== undefined && !isTodoAlarmResponseError(error)) return false;
  return true;
}

export function makeEnvelope(type: MessageType, init: EnvelopeInit): Envelope {
  const envelope: Envelope = {
    v: PROTOCOL_VERSION,
    id: init.id,
    type,
    ts: init.ts ?? Math.floor(Date.now() / 1000),
  };
  if (init.device_id !== undefined) envelope.device_id = init.device_id;
  if (init.nonce !== undefined) envelope.nonce = init.nonce;
  if (init.ciphertext !== undefined) envelope.ciphertext = init.ciphertext;
  if (init.payload !== undefined) envelope.payload = init.payload;
  return envelope;
}
